/**
 * Rental cart bridge
 *
 * Intercepts the product-rental form submit. Detects rental vs resale intent
 * via window.getRentalIntent() + the BUY radio. Creates the rental or resale
 * hold against the Endless API, appends the cart-transform required line item
 * properties (_rental_price + _rental_signature etc), then POSTs to /cart/add.js.
 * On success dispatches cart-update events the Broadcast theme + cart-monitor.js
 * listen for. On failure surfaces the error via the existing
 * .product-form-text__error span and dispatches cart-error.
 *
 * Non-rental + non-resale products fall through to native form submit.
 */

import * as RentalUtils from './rental-helpers.js';
import { createRentalHold } from './rental-api.js';
import { createResaleHold } from './resale-ops.js';
import { LOG_PREFIXES, getApiUrl } from './api-config.js';

const form = document.querySelector('.product-rental__form');
if (form) {
  initCartBridge(form);
}

function initCartBridge(form) {
  const atc = form.querySelector('[ref="addToCartButton"]');
  const errorEl = form.querySelector('.product-form-text__error');
  const apiUrl = getApiUrl('');

  const showError = (msg) => {
    if (!errorEl) return;
    // Always replace the full text content rather than assuming a specific
    // child-node structure — errorEl is a plain empty <span>/<p> in markup
    // (no icon or leading node), so the previous childNodes[2] guess never
    // matched and every call fell through to appendChild, stacking a new
    // message on every repeated Add to Cart click instead of replacing it.
    errorEl.textContent = msg;
    errorEl.classList.remove('hidden');
  };

  const clearError = () => {
    if (!errorEl) return;
    errorEl.classList.add('hidden');
  };

  const setLoading = (on) => {
    if (!atc) return;
    atc.disabled = on;
    atc.classList.toggle('is-loading', on);
  };

  const dispatchCartUpdate = (detail) => {
    document.dispatchEvent(new CustomEvent('cart:update', { detail }));
    document.dispatchEvent(new CustomEvent('cart-update', { detail }));
    if (typeof window.publish === 'function' && window.theme?.PUB_SUB_EVENTS?.cartUpdate) {
      window.publish(window.theme.PUB_SUB_EVENTS.cartUpdate, detail);
    }
    // Broadcast theme: refresh cart contents then open drawer.
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('theme:product:added', { bubbles: true }));
  };

  const dispatchCartError = (message) => {
    const detail = { message };
    document.dispatchEvent(new CustomEvent('cart:error', { detail }));
    document.dispatchEvent(new CustomEvent('cart-error', { detail }));
    if (typeof window.publish === 'function' && window.theme?.PUB_SUB_EVENTS?.cartError) {
      window.publish(window.theme.PUB_SUB_EVENTS.cartError, detail);
    }
  };

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();

    const rentalIntent = typeof window.getRentalIntent === 'function' ? window.getRentalIntent() : null;
    const buyRadio = document.querySelector('[data-buy-item-radio]');
    const resaleIntent = !!(buyRadio && buyRadio.checked);

    if (!rentalIntent && !resaleIntent) {
      showError('Please select RENT or BUY first.');
      return;
    }

    clearError();
    setLoading(true);

    const formData = new FormData(form);
    const variantId = String(formData.get('id') || '');

    try {
      if (rentalIntent) {
        const rentalData = typeof window.getRentalData === 'function' ? window.getRentalData() : null;
        if (!rentalData) {
          showError('Please select valid rental dates');
          dispatchCartError('Please select valid rental dates');
          setLoading(false);
          return;
        }
        const rentalPrice = Number(rentalData.price);
        if (!rentalPrice || isNaN(rentalPrice) || rentalPrice <= 0) {
          showError('Unable to calculate rental price. Please refresh and try again.');
          dispatchCartError('Unable to calculate rental price');
          setLoading(false);
          return;
        }
        if (!rentalData.start_date || !rentalData.end_date || !rentalData.days) {
          showError('Invalid rental data. Please refresh and try again.');
          dispatchCartError('Invalid rental data');
          setLoading(false);
          return;
        }

        console.log(`${LOG_PREFIXES.RENTAL} Creating Hold...`);
        const holdData = await createRentalHold(rentalData, variantId, apiUrl);

        if (!holdData.holdUid || !holdData.rentalPrice || !holdData.priceSignature) {
          const msg = holdData.error || 'Unable to reserve item. Please try again.';
          showError(msg);
          dispatchCartError(msg);
          setLoading(false);
          return;
        }

        formData.append('properties[_is_rental]', 'true');
        formData.append('properties[_rental_price]', holdData.rentalPrice);
        formData.append('properties[_rental_start_date]', rentalData.start_date);
        formData.append('properties[_rental_end_date]', rentalData.end_date);
        formData.append('properties[_rental_days]', String(rentalData.days));
        formData.append('properties[_rental_hold_uid]', holdData.holdUid);
        formData.append('properties[_rental_signature]', holdData.priceSignature);
        if (holdData.basePrice != null) {
          formData.append('properties[_rental_base_price]', String(holdData.basePrice));
        }
        if (holdData.customerFeesSnapshot && holdData.customerFeesSnapshot.length > 0) {
          formData.append('properties[_rental_fees_bundle]', JSON.stringify(holdData.customerFeesSnapshot));
        }
        if (rentalData.owner_uid) {
          formData.append('properties[_rental_owner_uid]', rentalData.owner_uid);
        }
        formData.append('properties[_rental_tag]', 'Rental');
        formData.append(
          'properties[Dates]',
          `${RentalUtils.formatDateForDisplay(rentalData.start_date)} - ${RentalUtils.formatDateForDisplay(rentalData.end_date)}`
        );
        formData.append('properties[Duration]', `${rentalData.days} day${rentalData.days !== 1 ? 's' : ''}`);
        if (rentalData.owner_uid) {
          const isPartner = String(rentalData.owner_uid).toUpperCase().startsWith('PART_');
          const nameLower = String(rentalData.owner_name || '').toLowerCase();
          const managedBy = isPartner && nameLower.includes('endless') ? 'Endless' : 'Owner';
          formData.append('properties[_Managed by]', managedBy);
        }
        if (holdData.basePrice != null) {
          formData.append('properties[Rental Price]', `AED ${Math.round(parseFloat(holdData.basePrice) || 0)}`);
        }
        if (holdData.customerFeesSnapshot && holdData.customerFeesSnapshot.length > 0) {
          holdData.customerFeesSnapshot.forEach((fee) => {
            const label = fee.name || 'Fee';
            const amount = Math.round(parseFloat(fee.amount) || 0);
            formData.append(`properties[${label}]`, `AED ${amount}`);
          });
        }
      } else if (resaleIntent) {
        const resaleData = typeof window.getResaleData === 'function' ? window.getResaleData() : null;
        if (!resaleData) {
          showError('Item not available for purchase. Please refresh and try again.');
          dispatchCartError('Item not available for purchase');
          setLoading(false);
          return;
        }
        const resalePrice = Number(resaleData.price);
        if (!resalePrice || isNaN(resalePrice) || resalePrice <= 0) {
          showError('Item not available for purchase. Please refresh and try again.');
          dispatchCartError('Item not available for purchase');
          setLoading(false);
          return;
        }

        console.log(`${LOG_PREFIXES.RESALE} Creating Hold...`);
        const holdData = await createResaleHold(resaleData, String(resaleData.variant_id || variantId), apiUrl);

        if (!holdData.holdUid || !holdData.resalePrice || !holdData.priceSignature) {
          const msg = holdData.error || 'Unable to reserve item. Please try again.';
          showError(msg);
          dispatchCartError(msg);
          setLoading(false);
          return;
        }

        // Buy the resale (Buy) variant, not the selected rental variant — its base
        // price is the resale price, so the cart line shows the correct amount.
        formData.set('id', String(resaleData.variant_id || variantId));
        formData.append('properties[_is_resale]', 'true');
        formData.append('properties[_resale_price]', holdData.resalePrice);
        formData.append('properties[_resale_hold_uid]', holdData.holdUid);
        formData.append('properties[_resale_signature]', holdData.priceSignature);
        if (resaleData.owner_uid) {
          formData.append('properties[_resale_owner_uid]', resaleData.owner_uid);
        }
        formData.append('properties[_purchase_tag]', 'Resale');
        if (resaleData.owner_uid) {
          const isPartner = String(resaleData.owner_uid).toUpperCase().startsWith('PART_');
          const nameLower = String(resaleData.owner_name || '').toLowerCase();
          const managedBy = isPartner && nameLower.includes('endless') ? 'Endless' : 'Owner';
          formData.append('properties[_Managed by]', managedBy);
        }
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.status) {
        const msg = data?.description || data?.message || 'Could not add to cart.';
        showError(msg);
        dispatchCartError(msg);
        setLoading(false);
        return;
      }

      if (typeof window.clearOwnerSelection === 'function') {
        window.clearOwnerSelection();
      }
      window.location.href = (window.theme?.routes?.cart_url) || '/cart';
    } catch (err) {
      console.error(`${LOG_PREFIXES.RENTAL} Cart bridge error:`, err);
      showError('Something went wrong. Please try again.');
      dispatchCartError('Cart add failed');
      setLoading(false);
    }
  });
}
